import { IProduct } from 'interfaces/product';
import * as _ from 'lodash';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import * as rxjs from 'rxjs';
import * as rxjsOperators from 'rxjs-operators';

import apiService, { ApiService } from './api';

export class ProductService {
  private selectedProduct$ = new BehaviorSubject<IProduct>(null);
  private products$ = new BehaviorSubject<IProduct[]>(null);
  private productsIds$ = new BehaviorSubject<number[]>(null);

  private updateSubscription: Subscription = null;

  constructor(private api: ApiService) {}
  public loadProductsAffiliate(): Observable<IProduct[]> {
    return this.api.get('/product/affiliate').pipe(
      rxjsOperators.take(1),
      rxjsOperators.tap(products => {
        this.products$.next(products);

        const productsIds = products.reduce((acumulator: number[], actualValue: IProduct) => {
          if (actualValue.children.length > 0) {
            const ids = actualValue.children.reduce((acumulator: number[], currentValue: IProduct) => {
              return [...acumulator, currentValue.id];
            }, []);
            return [...acumulator, ...ids];
          }
          return [...acumulator, actualValue.id];
        }, []);

        this.productsIds$.next(productsIds);
      })
    );
  }

  public setProducts(products: Array<IProduct>) {
    this.products$.next(products);
  }

  public getProducts(): Observable<any> {
    return this.products$.asObservable();
  }

  public getSelectedProduct() {
    return this.selectedProduct$.asObservable();
  }

  public setSelectedProduct(product: IProduct) {
    if (this.updateSubscription) {
      this.updateSubscription.unsubscribe();
    }

    this.selectedProduct$.next(product);

    if (!product) {
      return;
    }

    this.updateSubscription = this.selectedProduct$
      .asObservable()
      .pipe(
        rxjsOperators.skip(1),
        rxjsOperators.filter(selectedProduct => !!selectedProduct),
        rxjsOperators.debounceTime(1000),
        rxjsOperators.distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
        rxjsOperators.switchMap(data => this.saveProduct(data)),
        rxjsOperators.switchMap(data => {
          return this.getProducts().pipe(
            rxjsOperators.take(1),
            rxjsOperators.filter(() => !!data),
            rxjsOperators.map(products => {
              products.map((product: IProduct) => {
                if (product.children.length > 0) {
                  product.children.map(child => {
                    if (child.id == data.id) {
                      _.merge(child, data);
                    }
                  });
                } else {
                  if (product.id == data.id) {
                    _.merge(product, data);
                  }
                }
              });
              this.products$.next(products);
            })
          );
        })
      )
      .subscribe(() => {});
  }

  public setProductData(data: Partial<IProduct>) {
    this.getSelectedProduct()
      .pipe(rxjsOperators.take(1))
      .subscribe(product => {
        const newProduct = JSON.parse(JSON.stringify(product));
        _.merge(newProduct, data);
        this.selectedProduct$.next(newProduct);
      });
  }

  public saveProduct(data: IProduct): Observable<any> {
    return this.api.put(`/product/${data.id}`, data).pipe(rxjsOperators.switchMap(resp => rxjs.of(resp)));
  }

  public getProductsByProducerId(producerId: number): Observable<any> {
    return this.api.get(`/product/${producerId}`).pipe(rxjsOperators.switchMap(products => rxjs.of(products)));
  }
}

export const ProductServiceFactory = () => new ProductService(apiService);

export const productService = ProductServiceFactory();
